/**
 * Single resolve path for preview / PDF / print field values.
 *
 * Lookup priority when resolving a column on a row or header bag:
 *   1. Manual mapping column key (exact)
 *   2. Exact header match
 *   3. Case / whitespace / Vietnamese-normalized match
 *   4. Synonym match (deterministic dictionary — never fuzzy/uncontrolled)
 */

import { normalizeHeader, SYNONYM_MAP } from "./columnMatcher";
import { areSynonyms } from "./docxFieldMatcher";
import { formatFieldValue, type FieldFormattingConfig } from "./formatter";
import { looksLikeDateField } from "./formatter";

export type BindCase =
  /** Template field has no Excel column mapping. */
  | "unmapped"
  /** Mapped, but the mapped column is missing from the sheet headers. */
  | "mapped_missing_column"
  /** Mapped to a real column, but every looked-up value is blank. */
  | "mapped_empty"
  /** Mapped and at least one non-empty value was resolved. */
  | "mapped_ok"
  /** Explicitly marked as not used on this form (no auto-fill). */
  | "not_applicable";

export interface BindStatus {
  fieldName: string;
  excelColumn: string;
  case: BindCase;
  label: string;
  sample?: string;
}

export interface ResolveOptions {
  /** Preferred Excel column from (manual) mapping. Empty = unmapped. */
  excelColumn?: string;
  /** All sheet headers — used to validate the mapped column exists. */
  headers?: string[];
  /** Per-field formatting. */
  format?: FieldFormattingConfig;
  /**
   * When true, fields whose name looks like a date auto-apply DD/MM/YYYY even
   * if the user has not set formatType yet.
   */
  autoDateByName?: boolean;
  /** Force this field into the not_applicable case (no value filled). */
  notApplicable?: boolean;
}

export interface ResolveResult {
  value: string;
  raw: unknown;
  columnUsed: string;
  warnings: string[];
}

const BIND_LABELS: Record<BindCase, string> = {
  unmapped: "Chưa mapping",
  mapped_missing_column: "Cột không tồn tại",
  mapped_empty: "Đã map — ô trống",
  mapped_ok: "Đã bind dữ liệu",
  not_applicable: "Không áp dụng biểu mẫu này",
};

/**
 * Find a value on a record by exact key, then normalized / synonym match.
 * Manual `preferredColumn` always wins when present on the record.
 */
export function lookupRecordValue(
  record: Record<string, unknown> | undefined | null,
  preferredColumn: string,
  fieldName?: string
): { raw: unknown; columnUsed: string } {
  if (!record) return { raw: undefined, columnUsed: "" };

  if (preferredColumn && Object.prototype.hasOwnProperty.call(record, preferredColumn)) {
    return { raw: record[preferredColumn], columnUsed: preferredColumn };
  }

  const keys = Object.keys(record);
  if (preferredColumn) {
    const want = normalizeHeader(preferredColumn);
    for (const key of keys) {
      if (normalizeHeader(key) === want) {
        return { raw: record[key], columnUsed: key };
      }
    }
    for (const key of keys) {
      if (areSynonyms(want, normalizeHeader(key))) {
        return { raw: record[key], columnUsed: key };
      }
    }
  }

  if (fieldName) {
    const want = normalizeHeader(fieldName);
    for (const key of keys) {
      if (normalizeHeader(key) === want) {
        return { raw: record[key], columnUsed: key };
      }
    }
    for (const key of keys) {
      if (areSynonyms(want, normalizeHeader(key))) {
        return { raw: record[key], columnUsed: key };
      }
    }
  }

  return { raw: undefined, columnUsed: preferredColumn || "" };
}

function isBlank(value: unknown): boolean {
  return value == null || String(value).trim() === "";
}

/**
 * Resolve one field value for render. Shared by fill / preview / PDF / print.
 */
export function resolveFieldValue(
  record: Record<string, unknown> | undefined | null,
  fieldName: string,
  options: ResolveOptions = {}
): ResolveResult {
  const warnings: string[] = [];

  if (options.notApplicable) {
    return { value: "", raw: undefined, columnUsed: "", warnings };
  }

  const preferred = (options.excelColumn ?? "").trim();
  const headers = options.headers;

  if (preferred && headers && headers.length > 0) {
    const exists = headers.some(
      (h) => h === preferred || normalizeHeader(h) === normalizeHeader(preferred)
    );
    if (!exists) {
      warnings.push(
        `Trường "${fieldName}" map tới cột "${preferred}" nhưng cột không có trên sheet.`
      );
      return { value: "", raw: undefined, columnUsed: preferred, warnings };
    }
  }

  const { raw, columnUsed } = lookupRecordValue(record, preferred, fieldName);

  let format = options.format;
  if (
    (!format?.formatType || format.formatType === "text") &&
    options.autoDateByName !== false &&
    looksLikeDateField(fieldName)
  ) {
    format = {
      ...format,
      formatType: "date",
      dateFormat: format?.dateFormat || "DD/MM/YYYY",
    };
  }

  const formatted = formatFieldValue(raw, format, warnings);
  return { value: formatted, raw, columnUsed, warnings };
}

/**
 * Classify bind status for the mapping UI / pre-export checks.
 */
export function classifyBindStatus(
  fieldName: string,
  excelColumn: string,
  headers: string[],
  samples: unknown[],
  options?: { notApplicable?: boolean }
): BindStatus {
  if (options?.notApplicable) {
    return {
      fieldName,
      excelColumn: "",
      case: "not_applicable",
      label: BIND_LABELS.not_applicable,
    };
  }

  const col = (excelColumn ?? "").trim();
  if (!col) {
    return {
      fieldName,
      excelColumn: "",
      case: "unmapped",
      label: BIND_LABELS.unmapped,
    };
  }

  const exists =
    headers.length === 0 ||
    headers.some((h) => h === col || normalizeHeader(h) === normalizeHeader(col));
  if (!exists) {
    return {
      fieldName,
      excelColumn: col,
      case: "mapped_missing_column",
      label: BIND_LABELS.mapped_missing_column,
    };
  }

  const filled = samples.find((v) => !isBlank(v));
  if (filled === undefined) {
    return {
      fieldName,
      excelColumn: col,
      case: "mapped_empty",
      label: BIND_LABELS.mapped_empty,
    };
  }

  return {
    fieldName,
    excelColumn: col,
    case: "mapped_ok",
    label: BIND_LABELS.mapped_ok,
    sample: String(filled),
  };
}

/** Expose synonym keys for tests / diagnostics. */
export function synonymGroups(): typeof SYNONYM_MAP {
  return SYNONYM_MAP;
}
